import { BeakerIcon, HistoryIcon, PackageIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { InventoryLotListRow } from "@/modules/inventory/domain/types";
import { receivePrimaryLabel } from "@/modules/inventory/presentation/lib/qty-unit-hint";
import { formatDateLocal } from "@/shared/lib/date-time";
import { formatLakDisplay } from "@/shared/lib/format-number";
import {
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@/shared/ui/data-table";
import { RowActions } from "@/shared/ui/RowActions";

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const NEAR_EXPIRY_DAYS = 30;

function getExpiryStatus(expiryDate: string | null): "expired" | "near" | "ok" {
  if (!expiryDate) return "ok";
  const d = new Date(expiryDate);
  if (Number.isNaN(d.getTime())) return "ok";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(d);
  exp.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();
  const expMs = exp.getTime();
  if (expMs < todayMs) return "expired";
  if (expMs <= todayMs + NEAR_EXPIRY_DAYS * MS_PER_DAY) return "near";
  return "ok";
}

type LotsTableProps = {
  data: InventoryLotListRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onViewMovements: (row: InventoryLotListRow) => void;
};

export function LotsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
  onViewMovements,
}: LotsTableProps) {
  const columns: TanstackReactTable.ColumnDef<InventoryLotListRow>[] = [
    {
      accessorKey: "branchName",
      header: "ສາຂາ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.branchName}
        </span>
      ),
      size: 120,
      enableSorting: false,
    },
    createSortableColumn<InventoryLotListRow>("itemType", "ປະເພດ", {
      size: 100,
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.itemType === "INGREDIENT" ? "default" : "secondary"
          }
        >
          {row.original.itemType === "INGREDIENT" ? (
            <BeakerIcon className="size-4" />
          ) : (
            <PackageIcon className="size-4" />
          )}
          {row.original.itemType === "INGREDIENT" ? "ວັດຖຸດິບ" : "ຕົວແບບ"}
        </Badge>
      ),
    }),
    createSortableColumn<InventoryLotListRow>("itemName", "ລາຍການ", {
      size: 160,
    }),
    createSortableColumn<InventoryLotListRow>("lotNo", "Lot No.", {
      size: 120,
    }),
    createSortableColumn<InventoryLotListRow>("expiryDate", "ວັນຫມົດອາຍຸ", {
      size: 130,
      cell: ({ row }) => {
        const expiry = row.original.expiryDate;
        const status = getExpiryStatus(expiry);
        const label = expiry ? formatDateLocal(expiry) : "–";
        if (status === "expired") {
          return (
            <div className="flex items-center gap-2">
              <span className="text-destructive">{label}</span>
              <Badge variant="destructive" className="font-normal">
                ຫມົດອາຍຸ
              </Badge>
            </div>
          );
        }
        if (status === "near" && expiry) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const exp = new Date(expiry);
          exp.setHours(0, 0, 0, 0);
          const daysLeft = Math.max(
            0,
            Math.ceil((exp.getTime() - today.getTime()) / MS_PER_DAY),
          );
          return (
            <div className="flex items-center gap-2">
              <span className="text-amber-700 dark:text-amber-400">
                {label}
              </span>
              <Badge
                variant="secondary"
                className="border-amber-500/50 bg-amber-500/10 font-normal text-amber-700 dark:text-amber-400"
              >
                {daysLeft} ວັນສຸດທ້າຍ
              </Badge>
            </div>
          );
        }
        return <span className="text-muted-foreground">{label}</span>;
      },
    }),
    createSortableColumn<InventoryLotListRow>("qtyRemaining", "ຍອດຄົງເຫຼືອ", {
      size: 100,
      cell: ({ row }) => {
        const view = receivePrimaryLabel(
          row.original.qtyRemaining,
          row.original.baseUnit,
          row.original.receiveUnit,
          row.original.receiveToBase,
        );
        return (
          <div>
            <div>{view.text}</div>
            {view.hint ? (
              <div className="text-muted-foreground text-xs">({view.hint})</div>
            ) : null}
          </div>
        );
      },
    }),
    createSortableColumn<InventoryLotListRow>("costPerUnitLak", "ຕົ້ນທຶນ/ຫນ່ວຍ", {
      size: 110,
      cell: ({ row }) => formatLakDisplay(row.original.costPerUnitLak),
    }),
    createSortableColumn<InventoryLotListRow>("receivedAt", "ວັນທີຮັບ", {
      size: 110,
      cell: ({ row }) => formatDateLocal(row.original.receivedAt),
    }),
    {
      size: 120,
      id: "actions",
      cell: ({ row }: { row: { original: InventoryLotListRow } }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງປະຫວັດ",
              icon: <HistoryIcon className="size-4" />,
              onClick: () => onViewMovements(row.original),
            },
          ]}
          maxInline={3}
        />
      ),
    },
  ];

  return (
    <DataTable<InventoryLotListRow, unknown>
      noDataMessage="ບໍ່ພົບ lot"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
